import { useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ContentCopyOutlined, LinkOff as RevocarIcon } from "@mui/icons-material";
import { useEditarTemaProceso } from "../../queries/useInstancias";
import { useEstudiante } from "../../queries/useEstudiantes";
import { useDocentes } from "../../queries/useTemas";
import { InstanciaPaso } from "../../types/instanciaPaso";
import { DocenteOpcion } from "../../types/tema";
import { mensajeDeError } from "../../utils/errores";
import RevocarTemaDialog from "./RevocarTemaDialog";

const ESTADOS_CERRADOS = ["aprobado", "reprobado", "eliminado"];

const opcion = (id: number | null, nombre: string | null): DocenteOpcion | null => (id ? { id, nombre: nombre ?? "" } : null);

/**
 * Poner o cambiar el tema de un proceso: título, profesor guía y coguía (coordinación).
 *
 * **Se escribe en el proceso, no en el catálogo.** El tema de un paso son sus propios
 * campos; el `Tema` del catálogo es opcional y no se toca. Por eso «asignar» no crea un
 * tema en el catálogo y «cambiar» no renombra el original.
 *
 * Si el estudiante tuvo tema en otro paso se ofrece traerlo: es el caso típico, un
 * Informe que tiene que seguir con el tema de la Propuesta. Trae también el enlace al
 * catálogo, si ese paso lo tenía.
 *
 * Asignar llega además a sus otros procesos abiertos sin tema (lo hace el backend);
 * cambiar corrige solo este paso.
 */
export default function TemaProcesoDialog({ it, onClose }: { it: InstanciaPaso; onClose: () => void }) {
  const asignando = !it.tema;
  const editar = useEditarTemaProceso();
  const { data: estudiante } = useEstudiante(it.estudiante_rut ?? NaN);

  const [titulo, setTitulo] = useState(it.titulo || it.tema || "");
  const [guia, setGuia] = useState<DocenteOpcion | null>(opcion(it.guia_id, it.guia));
  const [coguia, setCoguia] = useState<DocenteOpcion | null>(opcion(it.coguia_id, it.coguia));
  const [coguiaExterno, setCoguiaExterno] = useState(it.coguia_externo);
  const [coguiaExternoEmail, setCoguiaExternoEmail] = useState(it.coguia_externo_email);
  const [tema, setTema] = useState<number | null>(it.tema_id);
  const [revocar, setRevocar] = useState(false);
  // Revocar es la otra cara de cambiar: quitarle el tema al estudiante. Vive acá y no en
  // la tarjeta porque es una decisión sobre el tema, no un botón más a la vista.
  const puedeRevocar = !asignando && it.estudiante_rut != null;
  const [guiaQuery, setGuiaQuery] = useState("");
  const [coguiaQuery, setCoguiaQuery] = useState("");
  const { data: guias = [], isFetching: buscandoGuia } = useDocentes(guiaQuery, true, true);
  const { data: coguias = [], isFetching: buscandoCoguia } = useDocentes(coguiaQuery, true, true);

  const otros = estudiante?.instancias ?? [];
  // Los pasos de donde se puede traer el tema: los que tienen uno distinto del de acá.
  const fuentes = otros
    .filter((o) => o.id !== it.id && o.tema && o.tema !== it.tema)
    .sort((a, b) => b.ano - a.ano || b.semestre - a.semestre);
  // A cuántos procesos más llega asignar: los abiertos que tampoco tienen tema.
  const alcanzaA = asignando ? otros.filter((o) => o.id !== it.id && !o.tema && !ESTADOS_CERRADOS.includes(o.estado)).length : 0;

  const traer = (o: InstanciaPaso) => {
    setTitulo(o.tema ?? "");
    setGuia(opcion(o.guia_id, o.guia));
    setCoguia(opcion(o.coguia_id, o.coguia));
    setCoguiaExterno(o.coguia_externo);
    setCoguiaExternoEmail(o.coguia_externo_email);
    setTema(o.tema_id);
  };

  const hayExterno = coguiaExterno.trim() !== "" || coguiaExternoEmail.trim() !== "";
  const guiaEnComision = it.comision.some((m) => m.rol === "guia");
  const cambiaGuia = !asignando && guia?.id !== it.guia_id;
  const guardable = titulo.trim() !== "" && guia !== null && !editar.isPending;

  const guardar = () =>
    guia &&
    editar.mutate(
      {
        id: it.id,
        input: {
          titulo: titulo.trim(),
          guia: guia.id,
          coguia: coguia?.id ?? null,
          coguia_externo: coguia ? "" : coguiaExterno.trim(),
          coguia_externo_email: coguia ? "" : coguiaExternoEmail.trim(),
          tema,
        },
      },
      { onSuccess: onClose },
    );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {asignando ? "Asignar tema" : "Cambiar tema"} — {it.paso}
        <Typography variant="body2" color="text.secondary">
          {it.estudiante} · {it.ano} · {it.semestre_display}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {editar.isError && <Alert severity="error">{mensajeDeError(editar.error, "No se pudo guardar el tema.")}</Alert>}

          {fuentes.length > 0 && (
            <Stack spacing={0.75}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                Traer de otro paso
              </Typography>
              {fuentes.map((o) => (
                <Button
                  key={o.id}
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopyOutlined />}
                  onClick={() => traer(o)}
                  disabled={editar.isPending}
                  sx={{ justifyContent: "flex-start", textAlign: "left", textTransform: "none" }}
                >
                  {o.paso} · {o.ano} {o.semestre_display} — «{o.tema}»{o.guia ? `, con ${o.guia}` : ""}
                </Button>
              ))}
            </Stack>
          )}

          <TextField
            label="Título"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            required
            fullWidth
            multiline
            autoFocus={fuentes.length === 0}
            disabled={editar.isPending}
          />

          <Autocomplete
            options={guias}
            getOptionLabel={(d) => d.nombre}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={guia}
            onChange={(_, value) => setGuia(value)}
            onInputChange={(_, value, reason) => reason === "input" && setGuiaQuery(value)}
            loading={buscandoGuia}
            disabled={editar.isPending}
            filterOptions={(x) => x}
            noOptionsText={guiaQuery ? "Sin resultados" : "Escribe para buscar un profesor"}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Profesor guía"
                required
                placeholder="Buscar profesor…"
                helperText={cambiaGuia && guiaEnComision ? "El guía nuevo reemplaza al anterior en la comisión." : undefined}
              />
            )}
          />

          <Stack spacing={1.5}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
              Coguía (opcional) — interno o externo
            </Typography>
            <Autocomplete
              options={coguias}
              getOptionLabel={(d) => d.nombre}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={coguia}
              onChange={(_, value) => setCoguia(value)}
              onInputChange={(_, value, reason) => reason === "input" && setCoguiaQuery(value)}
              loading={buscandoCoguia}
              disabled={editar.isPending || hayExterno}
              filterOptions={(x) => x}
              noOptionsText={coguiaQuery ? "Sin resultados" : "Escribe para buscar un docente"}
              renderInput={(params) => <TextField {...params} label="Coguía interno" placeholder="Buscar docente…" />}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label="Coguía externo"
                value={coguiaExterno}
                onChange={(e) => setCoguiaExterno(e.target.value)}
                fullWidth
                disabled={editar.isPending || coguia !== null}
              />
              <TextField
                label="Email del coguía externo"
                type="email"
                value={coguiaExternoEmail}
                onChange={(e) => setCoguiaExternoEmail(e.target.value)}
                fullWidth
                disabled={editar.isPending || coguia !== null}
              />
            </Stack>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Se guarda en este proceso: el tema del catálogo, si lo hay, no cambia.
            {alcanzaA > 0 &&
              ` También se pone en ${alcanzaA === 1 ? "su otro proceso abierto" : `sus otros ${alcanzaA} procesos abiertos`} sin tema.`}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: puedeRevocar ? "space-between" : "flex-end" }}>
        {puedeRevocar && (
          <Button color="error" startIcon={<RevocarIcon />} onClick={() => setRevocar(true)} disabled={editar.isPending}>
            Revocar tema
          </Button>
        )}
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={editar.isPending}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={guardar} disabled={!guardable}>
            {editar.isPending ? "Guardando…" : asignando ? "Asignar tema" : "Guardar cambios"}
          </Button>
        </Stack>
      </DialogActions>

      {revocar && (
        <RevocarTemaDialog
          rut={it.estudiante_rut as number}
          estudiante={it.estudiante}
          tema={it.tema as string}
          onClose={() => setRevocar(false)}
          onRevocado={onClose}
        />
      )}
    </Dialog>
  );
}
